<template>
	<div
		class="items-center gap-1 rounded-full xs:gap-2"
		:class="[
			fullWidth ? 'flex w-full' : 'inline-flex',
			!iconButtonStyle && !lightTheme && 'border border-border-tertiary bg-button-secondary-surface shadow-green-sm p-0.5',
		]"
	>
		<button
			type="button"
			:disabled="(vModel <= min && !removable) || isLoading || disabled"
			class="focus-outline shrink-0 aspect-square"
			:class="iconButtonStyle
				? 'btn btn-secondary btn-icon text-icon-primary'
				: [
					'typo-body-xl rounded-full bg-button-secondary-surface text-icon-primary hover:bg-button-secondary-surface-hover active:bg-button-secondary-surface-active active:text-icon-active disabled:bg-button-secondary-surface disabled:text-body-disabled inline-flex items-center justify-center rounded-full',
					lightTheme && 'border border-border-tertiary shadow-xs',
					buttonSizeClass ? buttonSizeClass : 'h-8 sm:h-11',
				]"
			@click="onLeftClick"
		>
			<slot name="icon-left">
				<UiLoading :is-loading="isLoading">
					<span class="sr-only">{{ labelLeft }}</span>
					<span
						class="inline-flex items-center justify-center"
						:class="iconButtonStyle ? 'text-base' : 'w-[1.25rem] h-[1.25rem] typo-body-m'"
					>
						<NuxtIcon
							v-if="leftIcon"
							:name="leftIcon"
							filled
							:class="[
								iconButtonStyle && 'text-base',
								!iconButtonStyle && isRemoveMode && removeIconClass,
								isIconSwapAnimating && 'icon-swap-animate',
							]"
							@animationend="onIconSwapAnimationEnd"
						/>
					</span>
				</UiLoading>
			</slot>
		</button>
		<div
			v-if="fullWidth"
			class="flex flex-1 items-center justify-center min-w-0"
		>
			<slot v-bind="{value: vModel}">
				{{ vModel }}
			</slot>
		</div>
		<slot
			v-else
			v-bind="{value: vModel}"
		>
			{{ vModel }}
		</slot>
		<button
			type="button"
			:disabled="vModel >= max || isLoading || disabled"
			class="focus-outline shrink-0 aspect-square"
			:class="iconButtonStyle
				? 'btn btn-secondary btn-icon text-icon-primary'
				: [
					'typo-body-xl rounded-full bg-button-secondary-surface text-icon-primary hover:bg-button-secondary-surface-hover active:bg-button-secondary-surface-active active:text-icon-active disabled:bg-button-secondary-surface disabled:text-body-disabled inline-flex items-center justify-center rounded-full',
					lightTheme && 'border border-border-tertiary shadow-xs',
					buttonSizeClass ? buttonSizeClass : 'h-8 sm:h-11',
				]"
			@click="vModel += 1"
		>
			<slot name="icon-right">
				<UiLoading :is-loading="isLoading">
					<span class="sr-only">{{ labelRight }}</span>
					<span
						class="inline-flex items-center justify-center"
						:class="iconButtonStyle ? 'text-base' : 'w-[1.25rem] h-[1.25rem] typo-body-m'"
					>
						<NuxtIcon
							v-if="iconRight"
							:name="iconRight"
							filled
							:class="iconButtonStyle && 'text-base'"
						/>
					</span>
				</UiLoading>
			</slot>
		</button>
	</div>
</template>

<script setup lang="ts">
	interface Props {
		modelValue: number
		min?: number
		max?: number
		iconLeft?: string
		iconRight?: string
		removeIcon?: string
		removeIconClass?: string
		removable?: boolean
		lightTheme?: boolean
		isLoading?: boolean
		disabled?: boolean
		labelLeft?: string
		labelRight?: string
		fullWidth?: boolean
		iconButtonStyle?: boolean
		buttonSizeClass?: string
	}

	interface Emits {
		(e: 'update:modelValue', payload: number): void
		(e: 'remove'): void
	}

	const props = withDefaults(defineProps<Props>(), {
		min: 1,
		max: Infinity,
		removeIcon: 'trash-2',
		removeIconClass: '[&_svg]:!text-icon-primary',
		removable: false,
		isLoading: false,
		disabled: false,
	});
	const emit = defineEmits<Emits>();
	const vModel = useVModel(props, 'modelValue', emit);

	const isRemoveMode = computed(() => props.removable && vModel.value <= props.min);
	const leftIcon = computed(() => (isRemoveMode.value ? props.removeIcon : props.iconLeft));
	const isIconSwapAnimating = ref(false);

	watch(isRemoveMode, (value, previous) => {
		if (previous === undefined || value === previous) {
			return;
		}
		isIconSwapAnimating.value = false;
		nextTick(() => {
			isIconSwapAnimating.value = true;
		});
	});

	function onIconSwapAnimationEnd() {
		isIconSwapAnimating.value = false;
	}

	function onLeftClick() {
		if (isRemoveMode.value) {
			emit('remove');
			return;
		}
		vModel.value -= 1;
	}
</script>

<style scoped>
	.icon-swap-animate {
		animation: icon-swap-in 0.16s ease;
	}

	@keyframes icon-swap-in {
		from {
			opacity: 0;
			transform: scale(0.5) rotate(-90deg);
		}

		to {
			opacity: 1;
			transform: scale(1) rotate(0);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.icon-swap-animate {
			animation: none;
		}
	}
</style>
